// =============================================
// useTags HOOK - Manejo de Tags con APIs
// Sistema de Tags Coloridas
// =============================================

import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';

// Hook para obtener todas las tags disponibles
export const useAvailableTags = () => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchTags = async () => {
      try {
        console.log('🏷️ Fetching available tags...');
        const response = await api.get('/tags');
        setTags(response.data.data.tags || []);
        setError(null);
        console.log('✅ Available tags loaded:', response.data.data.tags?.length || 0);
      } catch (err) {
        console.error('❌ Error fetching tags:', err);
        setError(err.response?.data?.message || 'Error loading tags');
        toast.error('Error al cargar tags disponibles');
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, []);

  return { tags, loading, error, refetch: () => window.location.reload() };
};

// Hook para manejar tags de un contacto específico
export const useContactTags = (contactId) => {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const fetchContactTags = useCallback(async () => {
    if (!contactId) return;
    
    setLoading(true);
    try {
      console.log('🏷️ Fetching contact tags for:', contactId);
      const response = await api.get(`/tags/contacts/${contactId}`);
      setTags(response.data.data.tags || []);
      setError(null);
      console.log('✅ Contact tags loaded:', response.data.data.tags?.length || 0);
    } catch (err) {
      console.error('❌ Error fetching contact tags:', err);
      setError(err.response?.data?.message || 'Error loading contact tags');
    } finally {
      setLoading(false);
    }
  }, [contactId]);

  useEffect(() => {
    fetchContactTags();
  }, [fetchContactTags]);

  const addTags = useCallback(async (newTags) => {
    if (!contactId || !newTags || newTags.length === 0) return;
    
    try {
      console.log('🏷️ Adding tags to contact:', contactId, newTags);
      const response = await api.post(`/tags/contacts/${contactId}`, {
        tags: newTags
      });
      
      setTags(response.data.data.current_tags || []);
      toast.success(`${newTags.length} tag(s) adicionada(s)`);
      console.log('✅ Tags added successfully');
      return response.data.data.current_tags;
    } catch (err) {
      console.error('❌ Error adding tags:', err);
      const errorMessage = err.response?.data?.message || 'Error adding tags';
      toast.error(errorMessage);
      throw err;
    }
  }, [contactId]);

  const removeTags = useCallback(async (tagsToRemove) => {
    if (!contactId || !tagsToRemove || tagsToRemove.length === 0) return;
    
    try {
      console.log('🏷️ Removing tags from contact:', contactId, tagsToRemove);
      const response = await api.delete(`/tags/contacts/${contactId}`, {
        data: { tags: tagsToRemove }
      });
      
      setTags(response.data.data.current_tags || []);
      toast.success(`${tagsToRemove.length} tag(s) removida(s)`);
      console.log('✅ Tags removed successfully');
      return response.data.data.current_tags;
    } catch (err) {
      console.error('❌ Error removing tags:', err);
      const errorMessage = err.response?.data?.message || 'Error removing tags';
      toast.error(errorMessage);
      throw err;
    }
  }, [contactId]);

  const updateTags = useCallback(async (newTags) => {
    if (!contactId) return;
    
    const currentTagsSet = new Set(tags);
    const newTagsSet = new Set(newTags);
    
    // Tags para adicionar
    const tagsToAdd = newTags.filter(tag => !currentTagsSet.has(tag));
    
    // Tags para remover
    const tagsToRemove = tags.filter(tag => !newTagsSet.has(tag));
    
    try {
      // Remover tags primeiro
      if (tagsToRemove.length > 0) {
        await removeTags(tagsToRemove);
      }
      
      // Agregar nuevas tags
      if (tagsToAdd.length > 0) {
        await addTags(tagsToAdd);
      }
      
      // Si no hay cambios, actualizar el estado local
      if (tagsToAdd.length === 0 && tagsToRemove.length === 0) {
        setTags(newTags);
      }
      
    } catch (err) {
      // En caso de error, recargar las tags desde el servidor
      fetchContactTags();
      throw err;
    }
  }, [contactId, tags, addTags, removeTags, fetchContactTags]);

  return {
    tags,
    loading,
    error,
    addTags,
    removeTags,
    updateTags,
    refetch: fetchContactTags
  };
};

// Hook para buscar contactos por tags
export const useContactsByTags = (searchTags = [], options = {}) => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const searchContacts = useCallback(async (tags, searchOptions = {}) => {
    if (!tags || tags.length === 0) {
      setContacts([]);
      return;
    }
    
    setLoading(true);
    try {
      console.log('🔍 Searching contacts by tags:', tags);
      const queryParams = new URLSearchParams({
        tags: Array.isArray(tags) ? tags.join(',') : tags,
        match_all: searchOptions.matchAll || false,
        limit: searchOptions.limit || 50,
        offset: searchOptions.offset || 0
      });
      
      const response = await api.get(`/tags/search/contacts?${queryParams}`);
      setContacts(response.data.data.contacts || []);
      setError(null);
      console.log('✅ Contact search completed:', response.data.data.contacts?.length || 0);
    } catch (err) {
      console.error('❌ Error searching contacts by tags:', err);
      setError(err.response?.data?.message || 'Error searching contacts');
      setContacts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (searchTags && searchTags.length > 0) {
      searchContacts(searchTags, options);
    } else {
      setContacts([]);
    }
  }, [searchTags, searchContacts, options]);

  return {
    contacts,
    loading,
    error,
    searchContacts
  };
};

// Hook para estadísticas de tags
export const useTagStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchStatistics = async () => {
      try {
        console.log('📊 Fetching tag statistics...');
        const response = await api.get('/tags/statistics');
        setStatistics(response.data.data);
        setError(null);
        console.log('✅ Tag statistics loaded');
      } catch (err) {
        console.error('❌ Error fetching tag statistics:', err);
        setError(err.response?.data?.message || 'Error loading statistics');
      } finally {
        setLoading(false);
      }
    };

    fetchStatistics();
  }, []);

  return { statistics, loading, error };
};

// Hook para operaciones bulk de tags
export const useBulkTagOperations = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const bulkUpdateTags = useCallback(async (contactIds, tags, action = 'add') => {
    if (!contactIds || contactIds.length === 0 || !tags || tags.length === 0) {
      toast.error('IDs de contactos y tags son requeridos');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      console.log('🔄 Bulk tag operation:', action, contactIds.length, 'contacts', tags.length, 'tags');
      
      const response = await api.post('/tags/bulk', {
        contact_ids: contactIds,
        tags: tags,
        action: action
      });

      const actionMessages = {
        add: `Tags agregadas a ${response.data.data.affected_contacts} contacto(s)`,
        remove: `Tags removidas de ${response.data.data.affected_contacts} contacto(s)`,
        replace: `Tags reemplazadas en ${response.data.data.affected_contacts} contacto(s)`
      };

      toast.success(actionMessages[action] || 'Operación completada');
      console.log('✅ Bulk operation completed:', response.data.data);
      
      return response.data.data;
    } catch (err) {
      console.error('❌ Error in bulk tag operation:', err);
      const errorMessage = err.response?.data?.message || 'Error en operación bulk';
      setError(errorMessage);
      toast.error(errorMessage);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    bulkUpdateTags,
    loading,
    error
  };
};

// Hook principal que combina todas las funcionalidades
export const useTags = (contactId = null) => {
  const { tags: availableTags, loading: availableLoading } = useAvailableTags();
  const { 
    tags: contactTags, 
    loading: contactLoading, 
    updateTags,
    addTags,
    removeTags,
    refetch: refetchContactTags
  } = useContactTags(contactId);
  const { statistics, loading: statsLoading } = useTagStatistics();
  const { bulkUpdateTags, loading: bulkLoading } = useBulkTagOperations();

  return {
    // Available tags
    availableTags,
    availableLoading,
    
    // Contact tags
    contactTags,
    contactLoading,
    updateTags,
    addTags,
    removeTags,
    refetchContactTags,
    
    // Statistics
    statistics,
    statsLoading,
    
    // Bulk operations
    bulkUpdateTags,
    bulkLoading,
    
    // General loading state
    loading: availableLoading || contactLoading || statsLoading || bulkLoading
  };
};